import type { Push, Unsubscribe } from 'src/cqrs/client';

import { oneService } from './oneService';

/**
 * Push channel — a thin dispatcher on top of `OneService.subscribePush`. The
 * Graph hub delivers a single per-user push stream; this fans each message out
 * by its discriminator (`push.type`) to interested subscribers. Provider
 * modules register handlers via `onPush(type, handler)` and the state they own
 * (React Query cache, Redux) updates live without polling.
 *
 * Today the published contract only defines `HeartbeatPush`; more push types
 * (e.g. `OperationStatusPush`) arrive as the backend publishes them, so unknown
 * types are simply ignored rather than erroring.
 */
type PushOfType<T extends string> = Extract<Push, { type: T }>;
type TypedPushHandler = (push: Push) => void;

const handlers = new Map<string, Set<TypedPushHandler>>();
let off: Unsubscribe | null = null;
let starters = 0;
let lastHeartbeatAt: number | null = null;

/**
 * Register a handler for a single push `type`. Returns an unsubscribe. Starting
 * the underlying subscription is the caller's job (`startPushChannel`), so a
 * connection is only opened when something actually consumes pushes.
 */
export function onPush<T extends string>(
  type: T,
  handler: (push: PushOfType<T>) => void
): Unsubscribe {
  let set = handlers.get(type);
  if (!set) {
    set = new Set();
    handlers.set(type, set);
  }
  const wrapped = handler as TypedPushHandler;
  set.add(wrapped);
  return () => {
    set?.delete(wrapped);
  };
}

/** Epoch ms of the last heartbeat received, or null if none yet. */
export function getLastHeartbeatAt(): number | null {
  return lastHeartbeatAt;
}

function dispatch(push: Push): void {
  const type = (push as { type?: string }).type;
  if (!type) return;
  if (type === 'HeartbeatPush') lastHeartbeatAt = Date.now();
  handlers.get(type)?.forEach(handler => handler(push));
}

/**
 * Begin consuming the server push stream. Opens the Graph hub connection on the
 * first starter (unless the user forced HTTP-only transport) and ref-counts, so
 * the underlying subscription is torn down only when the *last* starter stops.
 * Returns a stop function that is safe to call once; extra calls are no-ops.
 */
export function startPushChannel(): Unsubscribe {
  starters += 1;
  if (!off) {
    off = oneService.subscribePush(dispatch);
  }
  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    starters -= 1;
    if (starters === 0 && off) {
      off();
      off = null;
    }
  };
}
